import React, { useState } from 'react'
import { ActionIcon, Button, Group, Menu, Stack, Text, Tooltip } from '@mantine/core'
import { useApplyRules, usePricingVersionCache } from '../../../../../js/hooks/global-job-matching'
import { IconArrowLeft, IconChevronDown } from '@tabler/icons-react'
import { useHistory, useParams } from 'react-router'
import { getShortDate } from '../../../../../js/lib/formatters'
import { PromoteMode } from '../../GlobalJobMatchingVersioning/types/versions'
import { UseMutationResult } from 'react-query'
import { useDisclosure } from '@mantine/hooks'
import { ApplyRulesModal } from './ApplyRulesModal'
import { GENERATE_REPORT_OPTIONS, GenerateReportModal } from './GenerateReportModal'
import { useAppState } from '../../../../hooks/redux'
import { EditableTitle } from './EditableTitle'
import { COLORS } from '../../../../lib/mantineTheme'

type TitleBarProps = {
  runTheRulesMutation: UseMutationResult
  polling: boolean
  stepIndex: number
}

export const modeText: { [key in PromoteMode]: string } = {
  [PromoteMode.CREATE_NEW]: 'Create new matches',
  [PromoteMode.OVERWRITE_EXISTING]: 'Update existing matches',
  [PromoteMode.OVERWRITE_ALL_AND_PRESERVE]: 'Replace matches'
}

export const TitleBar = ({ stepIndex }: TitleBarProps) => {
  const history = useHistory()
  const params = useParams<{ id: string }>()
  const { session: { csrfPair, subsite }} = useAppState()
  const version = usePricingVersionCache(params.id)
  const [selectedReportOption, setSelectedReportOption] = useState<GENERATE_REPORT_OPTIONS | undefined>(undefined)
  const [selectedMode, setSelectedMode] = useState<PromoteMode | undefined>(undefined)
  const { mutate: applyRules, isLoading: isApplyingRules, isSuccess: applySuccess, reset } = useApplyRules()
  const [generateReportModalOpened, { open: openGenerateReportModal, close: closeGenerateReportModal }] = useDisclosure(false)
  const [applyRulesModalOpened, { open: openApplyRulesModal, close: closeApplyRulesModal }] = useDisclosure(false)

  const handleGenerateReportClick = (option: GENERATE_REPORT_OPTIONS) => {
    setSelectedReportOption(option)
    openGenerateReportModal()
  }

  const handleApplyRulesClick = (mode: PromoteMode) => {
    setSelectedMode(mode)
    openApplyRulesModal()
  }

  if (applySuccess) {
    closeApplyRulesModal()
    reset()
  }

  return (
    <Group justify='space-between' py='lg' px='xl'>
      { selectedReportOption &&
      <GenerateReportModal opened={generateReportModalOpened} reportOption={selectedReportOption} onClose={closeGenerateReportModal} />
      }
      { selectedMode &&
      <ApplyRulesModal
        opened={applyRulesModalOpened}
        onClose={closeApplyRulesModal} mode={selectedMode}
        applyRules={(mode: PromoteMode) => applyRules({ mode, id: version.id })}
        isApplyingRules={isApplyingRules}
      />
      }
      <Group gap='lg'>
        <ActionIcon size={42} style={{ borderColor: COLORS.BORDER }} variant="default" onClick={() => history.replace('/market-analysis/strategy-based-pricing')}>
          <IconArrowLeft size={18} />
        </ActionIcon>
        <Stack gap='0' h='100%'>
          <EditableTitle version={version} />
          <Text style={{ fontSize: 12 }}>Last modified {getShortDate(version?.lastModified ?? '')}</Text>
        </Stack>
      </Group>

      <Group>
        { !version?.requiresRun && stepIndex === 4 &&
        <>
          <Menu width={200} shadow="md">
            <Menu.Target>
              <Button variant='transparent' rightSection={<IconChevronDown size={14} />}>Generate Report</Button>
            </Menu.Target>

            <Menu.Dropdown w={600}>
              <Menu.Item onClick={() => handleGenerateReportClick(GENERATE_REPORT_OPTIONS.PRICING_VERSION_DETAILS)}>
                <Text c='blue'>{GENERATE_REPORT_OPTIONS.PRICING_VERSION_DETAILS}</Text>
              </Menu.Item>
              <Menu.Item onClick={() => handleGenerateReportClick(GENERATE_REPORT_OPTIONS.PRICING_VERSION_SUMMARY)}>
                <Text c='blue'>{GENERATE_REPORT_OPTIONS.PRICING_VERSION_SUMMARY}</Text>
              </Menu.Item>
              <Menu.Item onClick={() => window.location.href = `${window.location.origin}/${subsite}/api/market-pricing/global-job-pricing/downloadRunTheRules?versionId=${version.id}&csrf_token=${csrfPair.csrf_token}&csrf_timestamp=${csrfPair.csrf_timestamp}`}>
                <Text c='blue'>{GENERATE_REPORT_OPTIONS.PRICING_VERSION_MATCH_PRIORITIES}</Text>
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>

          <Menu width={200} shadow="md">
            <Menu.Target>
              <Button variant='outline' size='lg' rightSection={<IconChevronDown size={14} />} disabled={isApplyingRules}>
                {isApplyingRules ? 'Applying...' : 'Apply to Jobs'}
              </Button>
            </Menu.Target>

            <Menu.Dropdown w={600}>
              <Tooltip
                w={340}
                transitionProps={{ duration: 200 }}
                multiline
                position='bottom'
                label='Creates new matches for existing priced jobs and assigns new matches to previously unpriced jobs. Existing matches will remain unchanged, and duplicates will not be created.'
              >
                <Menu.Item onClick={() => handleApplyRulesClick(PromoteMode.CREATE_NEW)}>
                  <Text c='blue'>{modeText[PromoteMode.CREATE_NEW]}</Text>
                </Menu.Item>
              </Tooltip>
              <Tooltip
                w={340}
                transitionProps={{ duration: 200 }}
                multiline
                position='bottom'
                label='Replaces existing matches from the same survey and survey job if the data cut differs. Matches not affected by version rules will remain unchanged.'
              >
                <Menu.Item onClick={() => handleApplyRulesClick(PromoteMode.OVERWRITE_EXISTING)}>
                  <Text c='blue'>{modeText[PromoteMode.OVERWRITE_EXISTING]}</Text>
                </Menu.Item>
              </Tooltip>
              <Tooltip
                w={340}
                transitionProps={{ duration: 200 }}
                multiline
                position='bottom'
                label='Removes all existing matches for jobs included in the version rules and applies matches where applicable. Only those matches included in the rules here will be made — all others will be removed.'
              >
                <Menu.Item onClick={() => handleApplyRulesClick(PromoteMode.OVERWRITE_ALL_AND_PRESERVE)}>
                  <Text c='blue'>{modeText[PromoteMode.OVERWRITE_ALL_AND_PRESERVE]}</Text>
                </Menu.Item>
              </Tooltip>
            </Menu.Dropdown>
          </Menu>
        </>
        }
      </Group>

    </Group>
  )
}
